import { Plus, Trash2, GripVertical } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

/**
 * Editor for the Strategic Advantage comparison table.
 * Value shape: {
 *   col_feature_header: string,
 *   col_generalist_header: string,
 *   col_us_header: string,
 *   rows: [{ k: string, g: string, p: string }, ...]
 * }
 */
export default function StrategicAdvantageEditor({ value, onChange, brandName }) {
  const v = value || {};
  const rows = Array.isArray(v.rows) ? v.rows : [];

  const update = (patch) => onChange({ ...v, ...patch });
  const updateRow = (i, patch) => {
    const next = rows.slice();
    next[i] = { ...next[i], ...patch };
    update({ rows: next });
  };
  const addRow = () => update({ rows: [...rows, { k: "", g: "", p: "" }] });
  const removeRow = (i) => update({ rows: rows.filter((_, idx) => idx !== i) });
  const moveRow = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = rows.slice();
    [next[i], next[j]] = [next[j], next[i]];
    update({ rows: next });
  };

  return (
    <div className="space-y-4" data-testid="strategic-advantage-editor">
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <Label htmlFor="sa-col1" className="text-xs font-semibold text-slate-700">Column 1 header (Feature)</Label>
          <Input
            id="sa-col1"
            value={v.col_feature_header ?? ""}
            onChange={(e) => update({ col_feature_header: e.target.value })}
            placeholder="(leave blank for empty header)"
            data-testid="sa-col-feature-header"
          />
        </div>
        <div>
          <Label htmlFor="sa-col2" className="text-xs font-semibold text-slate-700">Column 2 header</Label>
          <Input
            id="sa-col2"
            value={v.col_generalist_header ?? ""}
            onChange={(e) => update({ col_generalist_header: e.target.value })}
            placeholder="Generalist Agencies"
            data-testid="sa-col-generalist-header"
          />
        </div>
        <div>
          <Label htmlFor="sa-col3" className="text-xs font-semibold text-slate-700">Column 3 header (Us)</Label>
          <Input
            id="sa-col3"
            value={v.col_us_header ?? ""}
            onChange={(e) => update({ col_us_header: e.target.value })}
            placeholder={brandName || "Pharmaco Hires"}
            data-testid="sa-col-us-header"
          />
          <p className="mt-1 text-[11px] text-slate-500">Leave blank to auto-use the brand name.</p>
        </div>
      </div>

      <div className="space-y-3">
        {rows.length === 0 && (
          <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
            No rows yet — add one below.
          </div>
        )}
        {rows.map((row, i) => (
          <div key={i} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`sa-row-${i}`}>
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
                <GripVertical className="h-3.5 w-3.5" /> Row {i + 1}
              </div>
              <div className="flex items-center gap-1">
                <Button type="button" variant="ghost" size="sm" onClick={() => moveRow(i, -1)} disabled={i === 0} data-testid={`sa-row-${i}-up`}>↑</Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => moveRow(i, 1)} disabled={i === rows.length - 1} data-testid={`sa-row-${i}-down`}>↓</Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => removeRow(i)} className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`sa-row-${i}-remove`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-3">
              <div>
                <Label className="text-xs text-slate-600">Feature</Label>
                <Input value={row.k ?? ""} onChange={(e) => updateRow(i, { k: e.target.value })} placeholder="e.g. Network Access" data-testid={`sa-row-${i}-k`} />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Generalist column</Label>
                <Textarea rows={2} value={row.g ?? ""} onChange={(e) => updateRow(i, { g: e.target.value })} placeholder="What generalists offer" data-testid={`sa-row-${i}-g`} />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Us column</Label>
                <Textarea rows={2} value={row.p ?? ""} onChange={(e) => updateRow(i, { p: e.target.value })} placeholder="What we offer" data-testid={`sa-row-${i}-p`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <Button type="button" variant="outline" onClick={addRow} className="gap-1.5" data-testid="sa-add-row">
        <Plus className="h-3.5 w-3.5" /> Add row
      </Button>
    </div>
  );
}
